import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Star, Pencil, CheckCircle2, Circle, Link as LinkIcon, Copy, Image as ImageIcon, Wallet, Clock, Sparkles,
  Smartphone, Monitor, Printer, MapPin, Phone, Mail, Globe, BadgeCheck, MessageCircle, Share2, LayoutGrid,
  Building2, CalendarDays, Info, Images, ChevronRight, CalendarPlus,
} from 'lucide-react';
import { getMyGym } from '../api/gymApi';
import { getMyGymReviews } from '../api/reviewApi';
import { useToast } from '../components/ToastProvider';
import { WEEKDAYS } from '../components/FormKit';
import { formatTime12h } from '../utils/helpers';

const MAIN_DASHBOARD_URL = process.env.REACT_APP_MAIN_DASHBOARD_URL || '';

const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const TABS = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'photos', label: 'Photos', icon: Images },
  { id: 'amenities', label: 'Amenities', icon: LayoutGrid },
  { id: 'slots', label: 'Slots', icon: CalendarDays },
  { id: 'reviews', label: 'Reviews', icon: MessageCircle },
  { id: 'location', label: 'Location', icon: MapPin },
];

export default function ListingPreviewPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [gym, setGym] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [viewMode, setViewMode] = useState('desktop');

  useEffect(() => {
    Promise.all([
      getMyGym().catch(() => null),
      getMyGymReviews({ limit: 50 }).catch(() => null),
    ]).then(([gymData, reviewsData]) => {
      setGym(gymData);
      setReviews(Array.isArray(reviewsData?.reviews) ? reviewsData.reviews : []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8]">
          <CalendarPlus className="w-7 h-7" />
        </div>
        <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed]">No gym listing yet</h3>
        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-1">Create your gym to see how its listing will look.</p>
        <Link to="/my-gym" className="inline-block mt-4 text-sm font-semibold text-white px-5 py-2.5 rounded-full bg-[#1a73e8] hover:bg-[#1557b0]">
          Create Gym Listing
        </Link>
      </div>
    );
  }

  const checklist = [
    { label: 'Cover image uploaded', done: !!gym.coverImage },
    { label: 'Gallery photos added', done: (gym.images || []).length > 0 },
    { label: 'Description written', done: !!gym.description },
    { label: 'Contact details set', done: !!gym.phone && !!gym.email },
    { label: 'Pricing set', done: (gym.dailyPassPrice || 0) > 0 },
    { label: 'Amenities added', done: (gym.amenities || []).length > 0 },
    { label: 'Equipment listed', done: (gym.equipment || []).length > 0 },
    { label: 'House rules added', done: (gym.rules || []).length > 0 },
    { label: 'Weekly slots configured', done: WEEKDAYS.some((d) => (gym.weeklySlots?.[d] || []).length > 0) },
  ];
  const completeCount = checklist.filter((c) => c.done).length;

  const avgReviewRating = reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : gym.averageRating || 0;
  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({ star, count: reviews.filter((r) => r.rating === star).length }));
  const reviewCount = gym.totalReviews || reviews.length;

  const previewUrl = MAIN_DASHBOARD_URL ? `${MAIN_DASHBOARD_URL}/gyms/${gym._id}` : `(set REACT_APP_MAIN_DASHBOARD_URL to get a real link)/gyms/${gym._id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(previewUrl);
      showToast('Preview link copied to clipboard.');
    } catch {
      showToast("Couldn't copy — your browser blocked clipboard access.", 'error');
    }
  };

  const shareLinks = [
    { id: 'whatsapp', label: 'WhatsApp', color: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(`Check out ${gym.name} on GymPortal: ${previewUrl}`)}` },
    { id: 'twitter', label: 'X', color: '#0f1419', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${gym.name} on GymPortal`)}&url=${encodeURIComponent(previewUrl)}` },
    { id: 'facebook', label: 'Facebook', color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewUrl)}` },
    { id: 'email', label: 'Email', color: '#5f6368', href: `mailto:?subject=${encodeURIComponent(`Check out ${gym.name}`)}&body=${encodeURIComponent(previewUrl)}` },
  ];

  const gallery = [gym.coverImage, ...(gym.images || [])].filter(Boolean);
  const currencySymbol = gym.currency === 'INR' ? '₹' : `${gym.currency} `;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Listing Preview</h2>
          <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">How your gym appears to members.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center p-1 rounded-full bg-[#f1f3f4] dark:bg-[#202124]">
            <button onClick={() => setViewMode('desktop')} title="Desktop preview"
              className={`p-1.5 rounded-full cursor-pointer transition-colors ${viewMode === 'desktop' ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#9aa0a6]'}`}>
              <Monitor className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setViewMode('mobile')} title="Mobile preview"
              className={`p-1.5 rounded-full cursor-pointer transition-colors ${viewMode === 'mobile' ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#9aa0a6]'}`}>
              <Smartphone className="w-3.5 h-3.5" />
            </button>
          </div>
          <button onClick={() => window.print()}
            className="px-3 py-2 rounded-full text-xs font-semibold bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#3c4043] cursor-pointer flex items-center gap-1.5">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button onClick={() => navigate('/my-gym')}
            className="px-3 py-2 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Edit Gym
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Main preview card */}
        <div className="lg:col-span-8">
          <div className={viewMode === 'mobile' ? 'mx-auto max-w-[420px]' : ''}>
            <div className={`google-card bg-white dark:bg-[#303134] overflow-hidden ${
              viewMode === 'mobile' ? 'border-[6px] border-[#202124] dark:border-[#5f6368] rounded-[2rem] shadow-2xl' : 'border border-[#dadce0] dark:border-[#3c4043]'
            }`}>
              <div className="h-40 sm:h-52 bg-gradient-to-br from-[#1a73e8] via-[#4285f4] to-[#8ab4f8] relative">
                {gallery[0] && <img src={gallery[0]} alt={gym.name} className="absolute inset-0 w-full h-full object-cover" />}
                {gym.isActive && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/90 dark:bg-black/50 text-[#1e8e3e] backdrop-blur-sm">
                    <BadgeCheck className="w-3.5 h-3.5" /> {gym.verified ? 'Verified' : 'Active'}
                  </span>
                )}
              </div>

              <div className="p-5 sm:p-6 space-y-5">
                <div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] capitalize">
                    {gym.gymType}
                  </span>
                  <h2 className="text-2xl font-bold text-[#202124] dark:text-[#e8eaed] mt-2">{gym.name}</h2>
                  {gym.tagline && <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-0.5">{gym.tagline}</p>}
                  <div className="flex items-center gap-1 mt-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < Math.round(gym.averageRating || 0) ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
                    ))}
                    <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] ml-1">({reviewCount} reviews)</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {gym.yearsInBusiness > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6]">
                        <Building2 className="w-3.5 h-3.5" /> {gym.yearsInBusiness}+ yrs
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6]">
                      <MapPin className="w-3.5 h-3.5" /> {gym.city}, {gym.state}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 border-b border-[#e8eaed] dark:border-[#3c4043] pb-2 overflow-x-auto">
                  {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button key={t.id} onClick={() => setTab(t.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                          tab === t.id ? 'bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] font-semibold' : 'text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#202124]'
                        }`}>
                        <Icon className="w-3.5 h-3.5" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                {tab === 'overview' && (
                  <div className="space-y-6">
                    {gym.description && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-1.5">About</h3>
                        <p className="text-sm text-[#3c4043] dark:text-[#dadce0] leading-relaxed">{gym.description}</p>
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">Pricing</h3>
                      <div className="flex flex-wrap gap-3">
                        <div className="px-4 py-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                          <span className="text-[10px] text-[#9aa0a6] block">Daily</span>
                          <span className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">{currencySymbol}{gym.dailyPassPrice}</span>
                        </div>
                        {gym.weeklyPassPrice > 0 && (
                          <div className="px-4 py-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                            <span className="text-[10px] text-[#9aa0a6] block">Weekly</span>
                            <span className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">{currencySymbol}{gym.weeklyPassPrice}</span>
                          </div>
                        )}
                        {gym.monthlyPassPrice > 0 && (
                          <div className="px-4 py-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                            <span className="text-[10px] text-[#9aa0a6] block">Monthly</span>
                            <span className="text-sm font-bold text-[#202124] dark:text-[#e8eaed]">{currencySymbol}{gym.monthlyPassPrice}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {tab === 'photos' && (
                  <div>
                    {(gym.images || []).length === 0 ? (
                      <div className="text-center py-10">
                        <Images className="w-8 h-8 mx-auto text-[#dadce0] dark:text-[#3c4043]" />
                        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-2">No gallery photos yet.</p>
                        <button onClick={() => navigate('/my-gym')} className="mt-2 text-xs font-semibold text-[#1a73e8] dark:text-[#8ab4f8] hover:underline cursor-pointer">
                          Add photos in My Gym
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {gym.images.map((src) => (
                          <img key={src} src={src} alt="" className="w-full h-28 object-cover rounded-xl" />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'amenities' && (
                  <div className="space-y-6">
                    {(gym.amenities || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">Amenities</h3>
                        <div className="flex flex-wrap gap-2">
                          {gym.amenities.map((a) => (
                            <span key={a} className="px-3 py-1 rounded-full text-xs font-medium bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] capitalize">{a}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(gym.equipment || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">Equipment</h3>
                        <div className="flex flex-wrap gap-2">
                          {gym.equipment.map((e) => (
                            <span key={e} className="px-3 py-1 rounded-full text-xs font-medium bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] capitalize">{e}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(gym.features || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">Features</h3>
                        <div className="flex flex-wrap gap-2">
                          {gym.features.map((f) => (
                            <span key={f} className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-[#1a73e8] dark:text-[#8ab4f8]">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(gym.facilities || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">Facilities</h3>
                        <div className="flex flex-wrap gap-2">
                          {gym.facilities.map((f) => (
                            <span key={f} className="px-3 py-1 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-950/40 text-[#9334e6] dark:text-purple-300 capitalize">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(gym.rules || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#202124] dark:text-[#e8eaed] mb-2">House Rules</h3>
                        <ul className="space-y-1.5">
                          {gym.rules.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-[#3c4043] dark:text-[#dadce0]">
                              <CheckCircle2 className="w-4 h-4 text-[#34A853] shrink-0 mt-0.5" /> {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {tab === 'slots' && (
                  <div className="space-y-4">
                    {WEEKDAYS.map((day) => {
                      const slots = gym.weeklySlots?.[day] || [];
                      return (
                        <div key={day}>
                          <span className="text-[11px] font-bold uppercase tracking-wide text-[#5f6368] dark:text-[#9aa0a6]">{DAY_LABELS[day]}</span>
                          {slots.length === 0 ? (
                            <p className="text-xs text-[#9aa0a6] mt-1.5">Closed</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {slots.map((s) => (
                                <span key={s._id || `${s.startTime}-${s.endTime}`} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8]">
                                  {formatTime12h(s.startTime)}–{formatTime12h(s.endTime)} · cap {s.capacity}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {tab === 'reviews' && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-6 flex-wrap">
                      <div className="text-center shrink-0">
                        <div className="text-4xl font-bold text-[#202124] dark:text-[#e8eaed]">{avgReviewRating.toFixed(1)}</div>
                        <div className="flex justify-center mt-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(avgReviewRating) ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
                          ))}
                        </div>
                        <span className="text-[11px] text-[#9aa0a6] mt-1 block">{reviewCount} reviews</span>
                      </div>
                      {reviews.length > 0 && (
                        <div className="flex-1 min-w-[160px] space-y-1">
                          {ratingDistribution.map((d) => (
                            <div key={d.star} className="flex items-center gap-2 text-xs">
                              <span className="w-3 text-[#5f6368] dark:text-[#9aa0a6]">{d.star}</span>
                              <div className="flex-1 h-1.5 rounded-full bg-[#f1f3f4] dark:bg-[#202124] overflow-hidden">
                                <div className="h-full bg-[#FBBC05] rounded-full" style={{ width: `${(d.count / reviews.length) * 100}%` }} />
                              </div>
                              <span className="w-4 text-[#9aa0a6]">{d.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {reviews.length === 0 ? (
                      <p className="text-sm text-[#9aa0a6] text-center py-4">No reviews yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {reviews.slice(0, 3).map((r) => (
                          <div key={r._id} className="p-3.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-[#202124] dark:text-[#e8eaed]">{r.reviewerName}</span>
                              <span className="text-[10px] text-[#9aa0a6]">{r.createdAt?.slice(0, 10)}</span>
                            </div>
                            <div className="flex items-center gap-0.5 mt-1">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
                              ))}
                            </div>
                            {r.comment && <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-1.5">{r.comment}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    <button onClick={() => navigate('/reviews')} className="text-xs font-semibold text-[#1a73e8] dark:text-[#8ab4f8] hover:underline flex items-center gap-1 cursor-pointer">
                      View all reviews <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {tab === 'location' && (
                  <div className="space-y-5">
                    <div className="space-y-2.5">
                      <a href={`tel:${gym.phone}`} className="flex items-center gap-2.5 text-sm text-[#3c4043] dark:text-[#dadce0] hover:text-[#1a73e8] dark:hover:text-[#8ab4f8]">
                        <Phone className="w-4 h-4 text-[#1a73e8] shrink-0" /> {gym.phone}
                      </a>
                      {gym.email && (
                        <a href={`mailto:${gym.email}`} className="flex items-center gap-2.5 text-sm text-[#3c4043] dark:text-[#dadce0] hover:text-[#1a73e8] dark:hover:text-[#8ab4f8]">
                          <Mail className="w-4 h-4 text-[#1a73e8] shrink-0" /> {gym.email}
                        </a>
                      )}
                      {gym.website && (
                        <a href={gym.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-sm text-[#3c4043] dark:text-[#dadce0] hover:text-[#1a73e8] dark:hover:text-[#8ab4f8]">
                          <Globe className="w-4 h-4 text-[#1a73e8] shrink-0" /> {gym.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      <div className="flex items-start gap-2.5 text-sm text-[#3c4043] dark:text-[#dadce0]">
                        <MapPin className="w-4 h-4 text-[#1a73e8] shrink-0 mt-0.5" />
                        <span>{gym.address}, {gym.city}, {gym.state} {gym.zipCode}</span>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-dashed border-[#dadce0] dark:border-[#3c4043] bg-[#f8fafd] dark:bg-[#202124]/50 p-6 text-center">
                      <MapPin className="w-6 h-6 mx-auto text-[#9aa0a6]" />
                      <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-2">
                        Map preview · {gym.location?.coordinates?.[1]}, {gym.location?.coordinates?.[0]}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="lg:col-span-4 space-y-4 print:hidden">
          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">Listing Score</h3>
              <span className="text-xs font-bold text-[#1a73e8] dark:text-[#8ab4f8]">{completeCount}/{checklist.length}</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-[#e8eaed] dark:bg-[#3c4043] overflow-hidden mb-3">
              <div className="h-full rounded-full bg-[#1a73e8] transition-all" style={{ width: `${(completeCount / checklist.length) * 100}%` }} />
            </div>
            <div className="space-y-2">
              {checklist.map((c) => (
                <button key={c.label} onClick={() => navigate('/my-gym')} className="w-full flex items-center gap-2 text-left cursor-pointer group">
                  {c.done ? <CheckCircle2 className="w-4 h-4 text-[#34A853] shrink-0" /> : <Circle className="w-4 h-4 text-[#dadce0] dark:text-[#3c4043] shrink-0" />}
                  <span className={`text-xs ${c.done ? 'text-[#3c4043] dark:text-[#dadce0]' : 'text-[#9aa0a6] group-hover:text-[#5f6368] dark:group-hover:text-[#9aa0a6]'}`}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3">Quick Actions</h3>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => navigate('/my-gym')}
                className="p-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] border border-[#e8eaed] dark:border-[#3c4043] text-left text-xs font-semibold text-[#1a73e8] transition-colors cursor-pointer flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5" /> Add photos
              </button>
              <button onClick={() => navigate('/my-gym')}
                className="p-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124] hover:bg-green-50 dark:hover:bg-green-950/30 border border-[#e8eaed] dark:border-[#3c4043] text-left text-xs font-semibold text-[#34A853] transition-colors cursor-pointer flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5" /> Update pricing
              </button>
              <button onClick={() => navigate('/pricing-slots')}
                className="p-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124] hover:bg-purple-50 dark:hover:bg-purple-950/30 border border-[#e8eaed] dark:border-[#3c4043] text-left text-xs font-semibold text-purple-600 dark:text-purple-400 transition-colors cursor-pointer flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Manage slots
              </button>
            </div>
          </div>

          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3 flex items-center gap-1.5">
              <LinkIcon className="w-4 h-4 text-[#1a73e8]" /> Share This Listing
            </h3>
            <div className="google-input-box px-3 py-2 flex items-center justify-between gap-2 mb-2.5">
              <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6] truncate">{previewUrl}</span>
            </div>
            <button onClick={copyLink} className="w-full py-2 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer flex items-center justify-center gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy Link
            </button>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#e8eaed] dark:border-[#3c4043]">
              <Share2 className="w-3.5 h-3.5 text-[#9aa0a6] shrink-0" />
              {shareLinks.map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" title={`Share via ${s.label}`}
                  className="flex-1 text-center py-1.5 rounded-lg text-[10px] font-semibold border border-[#e8eaed] dark:border-[#3c4043] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] transition-colors" style={{ color: s.color }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          {completeCount < checklist.length && (
            <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 flex items-start gap-2.5 text-xs text-amber-900 dark:text-amber-200">
              <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <span>Complete the checklist above to boost your visibility in search.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
